// src/Layout/AuthContext.js

import React, { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearOrgIndustryTypesCache, clearFrameworkMappingsCache, seedPermissionContext } from '../Utils/adminUserTypes';
import { clearOrgFeatures, storeOrgFeatures } from '../Components/FocusMode/Zavvy/orgFeatures';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("jwtToken"));
  const navigate = useNavigate();
  const login = (token) => {
    localStorage.setItem("jwtToken", token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("menuPermissions");
    clearOrgFeatures();
    clearOrgIndustryTypesCache();
    clearFrameworkMappingsCache();
    setIsAuthenticated(false);

    // Unload Intercom
    if (window.Intercom) {
      window.Intercom('shutdown');
    }

    // Force page reload to clear all component state
    window.location.href = "/";
  };
  const switchAccount = (userData, index, handleClose, getTabs) => {
    if (userData && userData.user) {
      // Clear both caches so the new org's project types and framework mappings are fetched fresh
      clearOrgIndustryTypesCache();
      clearFrameworkMappingsCache();

      // Set new account data
      localStorage.setItem("jwtToken", userData?.access_token);
      localStorage.setItem("subscription_status", userData?.user?.SubscriptionStatus);
      localStorage.setItem("Subscription_remaining_days", userData?.user?.SubscriptionRemainingDays);
      localStorage.setItem(
        "account_expiration_day",
        String(userData?.user?.AccountExpirationDay ?? "")
      );
      localStorage.setItem("organizationId", userData?.user?.OrganizationID);
      localStorage.setItem("organizationName", userData?.user?.OrgName);
      localStorage.setItem("userType", userData?.user?.Role);
      localStorage.setItem("userLogindata", JSON.stringify(userData?.user));
      if (userData?.user?.AccountID) {
        localStorage.setItem("accountId", userData.user.AccountID);
      }
      
      // Store menu permissions for sidebar access control
      if (userData?.menu_permissions) {
        localStorage.setItem("menuPermissions", JSON.stringify(userData.menu_permissions));
      } else {
        localStorage.removeItem("menuPermissions");
      }

      // Seed framework gating context from backend-enriched switch-account response
      seedPermissionContext(
        userData?.framework_menu_mappings ?? null,
        userData?.org_industry_types ?? null
      );
      storeOrgFeatures(userData?.org_features);
      // Clear project-related data first
      localStorage.setItem("mode", 'Pro Mode');
      localStorage.removeItem("projectID");
      localStorage.setItem("isProjectShow", "true");
      window.dispatchEvent(new CustomEvent('sidebar:orgSwitched'));

      handleClose();
      getTabs();

      // Get current path and search parameters
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;

      // Keep the current menu selection state if we're staying on the same view
      const currentMainMenu = localStorage.getItem("selectedMainMenu");
      const currentSubMenu = localStorage.getItem("selectedSubMenu");

      // Check if the subscription is expired
      if (userData?.user?.subscription_status === 'expired') {
        navigate('/focus-expired', { state: { data: 'focusmodeemp' } });
      } else {
        // Check if we're on a valid admin view path
        if (currentPath === '/adminlist') {
          // Use current search parameters to determine the view
          const searchParams = new URLSearchParams(currentSearch);
          const view = searchParams.get('view');

          if (view && currentMainMenu && currentSubMenu) {
            // Keep the menu selection state
            localStorage.setItem("selectedMainMenu", currentMainMenu);
            localStorage.setItem("selectedSubMenu", view || currentSubMenu);
          }

          // Keep the user on the same view
          navigate(currentPath + currentSearch);
        } else if (currentPath === '/focus-projecttimeline' || currentPath === '/view/focus') {
          // If in focus mode, set the mode correctly
          localStorage.setItem("mode", "Focus Mode");
          // The project was cleared above and a project timeline belongs to one project of the
          // old company, so land on the Focus Mode home instead. The chat keeps its query
          // (view, session) so the switch does not drop the user out of what they had open.
          navigate(currentPath === '/focus-projecttimeline' ? '/view/focus' : currentPath + (currentSearch || ''));
        } else {
          // For other pages, default to Dashboard
          localStorage.setItem("selectedMainMenu", "home");
          localStorage.setItem("selectedSubMenu", "Dashboard");
          navigate("/adminlist?view=Dashboard");
        }
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    setIsAuthenticated(!!token);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, switchAccount }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
